import { CampaignDisplayStatus } from "@signalist/shared-types";

/**
 * The display statuses that block turning campaigns off (PREC-3687 O4) — the frontend half of
 * `apps/api/src/list/outreach/campaigns-disable-guard.ts`, which is the authority and rejects the
 * `PATCH` regardless of what this pre-check decided. **Keep the two lists in sync.**
 *
 * Only the states in which the list is actually sending: `ACTIVE`, and `LOADING` (an activation is
 * in flight and becomes ACTIVE on its own). `READY` means leads are assigned but the campaign was
 * never activated — it is not sending and cannot be paused, so blocking on it would strand the user.
 */
export const CAMPAIGN_DISABLE_BLOCKING_STATUSES: readonly CampaignDisplayStatus[] = [
	CampaignDisplayStatus.ACTIVE,
	CampaignDisplayStatus.LOADING,
];

/** The section's minimal view of a campaign entry — only the name is needed for the message. */
interface NamedCampaignEntry {
	name?: string;
}

/**
 * The names of the campaigns that stand in the way of switching campaigns off.
 *
 * Names are not part of the status map — they live in `list.campaignConfig[uuid].name` (there is no
 * campaign table), so the config is read alongside it and falls back to the uuid.
 */
export const blockingCampaignNames = (
	campaignStatuses?: Record<string, { status?: CampaignDisplayStatus }> | null,
	campaignConfig?: Record<string, NamedCampaignEntry> | null
): string[] =>
	Object.entries(campaignStatuses ?? {})
		.filter(
			([, info]) => !!info?.status && CAMPAIGN_DISABLE_BLOCKING_STATUSES.includes(info.status)
		)
		.map(([uuid]) => campaignConfig?.[uuid]?.name ?? uuid);
